import { CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { NumberFormatPreset } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DimPurchaseOrderField } from '~/common/types/cubes/DimPuchaseOrder';
import { DimPurchaseOrderItemField } from '~/common/types/cubes/DimPurchaseOrderItem';
import {
  ActiveCell,
  cellFactory,
  CurrencyCell,
  DataGridConfig,
  DataGridLinkVariant,
  GridAlignmentType,
  gridColDefFactory,
  LinkCell,
  LinkCellValue,
  numberFormatterFactory,
  UseDataGridConfig,
} from '~/components/DataGridCustom';
import { CuriositySourceReferenceCell } from '~/components/DataGridCustom/cells/CuriositySourceReferenceCell/CuriositySourceReferenceCell';
import { CuriosityValueCell } from '~/components/DataGridCustom/cells/CuriosityValueCell/CuriosityValueCell';
import { RouteId } from '~/routes';

export const usePurchaseOrderItemListGridConfig: UseDataGridConfig<any> = () => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(undefined, { keyPrefix: 'PurchaseOrderItemGrid.column' });

  return useMemo(() => {
    const createColumn = gridColDefFactory({ tColumn, t, defaultOptions: { sortable: false } });
    return {
      columnVisibilityModel: {},
      columns: [
        createColumn({
          field: DimPurchaseOrderItemField.PURCHASE_ORDER_ID,
          width: 120,
          renderCell: cellFactory(LinkCell),
          valueGetter: (_value, row): LinkCellValue => ({
            variant: DataGridLinkVariant.PRIMARY,
            to: RouteId.orderDetail(row[DimPurchaseOrderItemField.PURCHASE_ORDER_ID]),
            label: row[DimPurchaseOrderField.PRIMARY_SOURCE_REFERENCE],
          }),
        }),
        createColumn({ field: DimPurchaseOrderItemField.LINE_ITEM_NUMBER, width: 80 }),
        createColumn({ field: DimPurchaseOrderItemField.TITLE, width: 300 }),
        createColumn({
          field: DimPurchaseOrderField.SUPPLIER_ID,
          width: 150,
          renderCell: cellFactory(CuriositySourceReferenceCell, {
            ns: CuriosityNamespaceType.SUPPLIER,
            includeLink: true,
          }),
        }),
        createColumn({ field: DimPurchaseOrderItemField.DESCRIPTION, width: 300 }),

        createColumn({
          field: DimPurchaseOrderItemField.PRICE_PER_UNIT,
          minWidth: 150,
          align: GridAlignmentType.RIGHT,
          renderCell: cellFactory(CurrencyCell, ({ row }) => ({
            value: row[DimPurchaseOrderItemField.PRICE_PER_UNIT],
            currency: row[DimPurchaseOrderItemField.CURRENCY],
          })),
        }),
        createColumn({
          field: DimPurchaseOrderItemField.QUANTITY,
          minWidth: 150,
          align: GridAlignmentType.RIGHT,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.DECIMAL }),
        }),
        createColumn({
          field: DimPurchaseOrderItemField.UNIT_ID,
          minWidth: 150,
          renderCell: cellFactory(CuriosityValueCell, {
            ns: CuriosityNamespaceType.UNIT,
          }),
        }),
        createColumn({
          field: DimPurchaseOrderItemField.VALUE,
          minWidth: 150,
          align: GridAlignmentType.RIGHT,
          renderCell: cellFactory(CurrencyCell, ({ row }) => ({
            value: row[DimPurchaseOrderItemField.VALUE],
            currency: row[DimPurchaseOrderItemField.CURRENCY],
          })),
        }),
        createColumn({
          field: DimPurchaseOrderItemField.COMMODITY_GROUP_ID,
          minWidth: 150,
          renderCell: cellFactory(CuriosityValueCell, {
            ns: CuriosityNamespaceType.COMMODITY_GROUP,
          }),
        }),
        createColumn({
          field: DimPurchaseOrderItemField.COMMODITY_ID,
          minWidth: 150,
          renderCell: cellFactory(CuriosityValueCell, {
            ns: CuriosityNamespaceType.COMMODITY,
          }),
        }),
        createColumn({
          field: DimPurchaseOrderItemField.ORGANIZATION_ID,
          minWidth: 200,
          renderCell: cellFactory(CuriosityValueCell, {
            ns: CuriosityNamespaceType.ORGANIZATION,
          }),
        }),
        createColumn({
          field: DimPurchaseOrderItemField.IS_INVOICE_FINAL,
          width: 150,
          renderCell: cellFactory(ActiveCell),
        }),
        createColumn({
          field: DimPurchaseOrderItemField.IS_DELIVERY_FINAL,
          width: 150,
          renderCell: cellFactory(ActiveCell),
        }),
        createColumn({
          field: DimPurchaseOrderItemField.IS_DELIVERY_RECEIVED,
          width: 150,
          renderCell: cellFactory(ActiveCell),
        }),
        createColumn({
          field: DimPurchaseOrderItemField.IS_INVOICE_RECEIVED,
          renderCell: cellFactory(ActiveCell),
          width: 150,
        }),
        createColumn({
          field: DimPurchaseOrderItemField.IS_INVOICE_EXAMINATION_REQUIRED,
          renderCell: cellFactory(ActiveCell),
          width: 150,
        }),
        createColumn({
          field: DimPurchaseOrderItemField.IS_RETURN_ITEM,
          renderCell: cellFactory(ActiveCell),
          width: 150,
        }),
      ],
      columnGroupingModel: [],
    } as DataGridConfig<any>;
  }, [t, tColumn]);
};
